import React, { useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { modeUIContext, showSuccessNotification, setIsEditingForm, showErrorNotification, hideSlideOutPanel } from '@moderepo/mode-ui-state';
import { GatewayDefinition, GatewayDriver, RobotInfo } from '@moderepo/mode-apis';
import { capitalizeAll, SlideOutComp, useModeFormStyle } from '@moderepo/mode-app-components';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById } from '@moderepo/mode-data-state-ritz';
import { fetchRCProjectGatewayCatalog, fetchRobotById, rcDataContext, selectProjectGatewayCatalog, selectRobotById, updateRobot }
    from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCRobotsListingContainer, RobotsTableHiddenFields } from './RCRobotsListingContainer';



const useStyle = makeStyles((theme: Theme) => {

    return {
        root: {
            width  : '100%',
            padding: theme.spacing(2),
        },

        robotTablePanel: {
            width   : '100%',
            overflow: 'hidden',
        },

        form: {
            height: 'auto',
        },

        formActionContainer: {
            padding: theme.spacing(2),
        },
    };
}, { name: 'AddPreprovisionedDeviceContainer' });



interface RCLinkRobotPreviewContainerProps {
    readonly projectId: number;
    readonly homeId: number;
    readonly deviceId: number;
}


/**
 * This container display a list of robots for the user to select to link to a device
 */
export const RCLinkRobotPreviewContainer = (props: RCLinkRobotPreviewContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const { projectId, homeId, deviceId } = props;
    const project = selectProjectById(dataState, projectId);
    const [selectedRobot, setSelectedRobot] = useState<RobotInfo | undefined>();
    const selectedDevice = selectProjectDeviceById(dataState, deviceId);
    const gatewayCatalog = selectProjectGatewayCatalog(rcDataState, projectId);
    const [isEditing, setIsEditing] = useState<boolean>(true);

    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const formClasses = useModeFormStyle();



    /**
     * This useEffect is used for loading gateway definition on component init
     */
    useEffect(() => {
        if (!gatewayCatalog && project) {
            rcDataDispatch(fetchRCProjectGatewayCatalog(project.id)).catch(() => {
                /* ignore error */
            });
        }
    }, [props.projectId, gatewayCatalog, project, rcDataDispatch]);


    /**
     * This useEffect is used for loading device data on component init
     */
    useEffect(() => {
        if (props.deviceId && !selectedDevice) {
            dataDispatch(fetchProjectDeviceById(props.projectId, props.deviceId)).catch(() => { /* ignore error */ });
        }
    }, [dataDispatch, selectedDevice, props.projectId, props.deviceId]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * This component is used for selecting a Robot to add to a device so we must turn on edit mode as soon as this component start
     */
    useEffect(() => {
        onChangeEditingMode(true);
    }, [onChangeEditingMode]);



    /**
     * Unselect the selected robot and update the URL
     */
    const unselectRobot = useCallback(() => {
        onChangeEditingMode(false);
        uiDispatch(hideSlideOutPanel());
    }, [onChangeEditingMode, uiDispatch]);


    /**
     * Create the set of filters to pass to the Robot listing component.
     */
    const robotListingFilters = useMemo(() => {
        return {
            homeId,
        };
    }, [homeId]);


    /**
     * Create the map of fields we want to hide from the Robot listing component. This list will be used for selecting a robot to add to a device
     * so we don't need to show too many fields.
     */
    const robotListingHiddenFields = useMemo((): RobotsTableHiddenFields => {
        return {
            preview                : true,
            homeId                 : true,
            deviceId               : true,
            status                 : true,
            overrideRobotDefinition: true,
            createdAt              : true,
            updatedAt              : true,
        };
    }, []);


    const onRobotSelected = useCallback((robot: RobotInfo) => {
        setSelectedRobot(robot);
    }, []);


    const onCancelClicked = useCallback(() => {
        unselectRobot();
    }, [unselectRobot]);


    /**
     * On Okay button click, we will make an API call to link the selected robot with the selected device by updating the robot's deviceId field.
     */
    const onOkayClicked = useCallback(async (event: React.MouseEvent<HTMLElement>): Promise<boolean> => {
        event.stopPropagation();
        event.preventDefault();

        if (selectedRobot && deviceId && gatewayCatalog && selectedDevice) {
            // First, we need to check if the robot can be linked to the gateway. Not all gateway works with any robot. Each gateway definition
            // has an array of drivers that specify which robot class it can work with therefore we need to check if the selectedRobot.drivers
            // is defined in the selected gateway's drivers array
            const selectedGateway = gatewayCatalog.find((gateway: GatewayDefinition) => {
                return gateway.deviceClass === selectedDevice.deviceClass;
            });

            // Then find the gateway driver that matches the robot.driverName. If the robot.driverName is not in one of the
            // gateway's driver, then the robot can't be linked to the selectedGateway
            const driver = selectedGateway && selectedGateway.drivers && selectedGateway.drivers?.find((d: GatewayDriver) => {
                return d.driverName === selectedRobot.driverName;
            });

            if (selectedGateway && driver) {
                // The, we need to reload the Robot's info to make sure it is not linked to another device, this can happen
                // if multiple agents try to link the same robot at the same time.
                try {
                    await rcDataDispatch(fetchRobotById(projectId, homeId, selectedRobot.id));
                    const robotInfo = selectRobotById(rcDataState, homeId, selectedRobot.id);
                    if (robotInfo && !robotInfo.deviceId) {
                        // if robot doesn't have a deviceId yet, set it to the current deviceId
                        await rcDataDispatch(updateRobot(projectId, homeId, selectedRobot.id, { deviceId }));
                        uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.robot' }));
                        unselectRobot();
                    } else if (robotInfo) {
                        // else show error
                        uiDispatch(showErrorNotification('notif.robot_not_available'));
                    }
                } catch (error) {
                    // ignore error because dataDispatch already handled the error
                }
            } else {
                // The selected gateway does not work with the selected robot class
                uiDispatch(showErrorNotification('notif.link_robot.robot_not_supported', {
                    robot_class  : selectedRobot.robotClass,
                    gateway_class: selectedDevice.deviceClass,
                }));
            }
        }

        return false;
    }, [selectedRobot, deviceId, gatewayCatalog, selectedDevice, rcDataDispatch, projectId, homeId, rcDataState, uiDispatch, unselectRobot]);


    const slideOutCompContent = useMemo(() => {
        return (
            <form className={clsx(formClasses.root, classes.form, isEditing && 'editing')}>
                <RCRobotsListingContainer
                    className={classes.robotTablePanel}
                    title={capitalizeAll(trans('panel.select_robot.title'))}
                    projectId={props.projectId}
                    homeId={homeId}
                    gatewayDependentRobotsOnly      // because we are linking a robot to device, we need to show gateway dependent robots only
                    canLinkRobots={false}
                    canCreateRobots={false}
                    hiddenFields={robotListingHiddenFields}
                    filters={robotListingFilters}
                    onRobotSelected={onRobotSelected}
                />
                <div className={clsx(formClasses.formActionContainer, classes.formActionContainer)}>
                    <div />
                    <div>
                        <Button
                            className={clsx(formClasses.formActionButton)}
                            size="small"
                            onClick={onCancelClicked}
                        >
                            {capitalizeAll(trans('label.cancel'))}
                        </Button>
                        <Button
                            className={clsx(formClasses.formActionButton)}
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={Boolean(!selectedRobot || selectedRobot.deviceId)}
                            onClick={onOkayClicked}
                            size="small"
                        >
                            {capitalizeAll(trans('label.select'))}
                        </Button>
                    </div>
                </div>
            </form>

        );
    }, [classes.form, classes.formActionContainer, classes.robotTablePanel, formClasses.formActionButton, formClasses.formActionContainer,
        formClasses.root, homeId, isEditing, onCancelClicked, onOkayClicked, onRobotSelected, props.projectId, robotListingFilters,
        robotListingHiddenFields, selectedRobot, trans]);

    return (
        <SlideOutComp
            compContent={slideOutCompContent}
            closable={false}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
